(ns app.front.view.party
  (:require [app.domain.contractor-registration.model :as cr-model]
            [app.domain.party.model :as party-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.party.common :as party-common]
            [app.front.view.party.edit :as edit]
            [app.front.view.party.search :as search]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx
  ::go-to-party-form
  (fn [_ [_ workspace-id form-id]]
    {:route/push {:route :view/form :params {:workspace-id workspace-id
                                             :form-id      form-id}}}))


(defn party-status-text
  "Returns the plain-text status label for a party, considering removal, registration,
   invite status, creator status, and invitation state. Pure — no side effects."
  [tr party-entity invite]
  (let [registered? (seq (cr-model/party-entity->registrations party-entity))
        status      (:invite/status invite)]
    (cond
      (party-model/removed? party-entity)
      (tr [:party/removed])

      registered?
      (tr [:workspace/registered-contractor])

      status
      (str (tr [:invite/invite]) " " (str/lower-case (tr [status])))

      (or (:party/workspace-creator? party-entity)
          (and (:party/user party-entity)
               (= (-> party-entity :party/user :db/id)
                  (-> party-entity :entity/parent :entity/creator :db/id))))
      (tr [:workspace/creator])

      ;; Linked parties that were never invited
      (-> party-entity :party/user)
      ""

      :else
      (tr [:invite/not-invited]))))


(defn party-status [tr party-entity invite]
  (let [party-id    (:db/id party-entity)
        status      (:invite/status invite)
        status-text (party-status-text tr party-entity invite)]
    [:div.mt-1.text-xs {:key   (str party-id "-section-wrapper")
                        :class [(case status
                                  :invite.status/declined "text-ui-red-100"
                                  :invite.status/pending "text-ui-violet-100"
                                  "text-ui-night-sky-100")]
                        :style {:min-height "12px"}}
     status-text]))


(defn party-email [{:keys [tr] :as ctx} party invite]
  (let [email-address (or (party-model/get-user-key ctx party :user/email)
                          (:party/email invite))]
    [:div.w-full.break-words
     [:a {:href       (str "mailto:" email-address)
          :aria-label (tr [:party/email-to]
                          [email-address])} email-address]]))


(defn party-view
  "Display application party information"
  [{:keys [tr lang] :as ctx} party-entity target & [missing-role-ids]]
  (r/with-let [*integration-contact-modal-open? (r/atom false)]
    (let [party-data     (party-model/merge-user-and-party party-entity)
          invite         (-> party-entity :invite/_party first)
          party-id       (:db/id party-entity)
          target-id      (:db/id target)
          can-integrate? (<sub [::process/signal-permitted?
                                target-id
                                [:process.signal/view-integration-contacts]])
          customer-id    (-> target :entity/parent :domain/authority-group :group/id)
          current-int-id (party-model/integration-id party-entity customer-id)
          active?        (party-model/active? party-entity)
          no-name?       (str/blank? (party-model/business-or-full-name party-entity lang))]
      [:div.party-elem-wrapper.mb-4.border-solid.border-l-4
       {:key   party-id
        :class (futil/css-flags "border-ui-green" (party-model/active? party-entity)
                                "border-ui-yellow" (party-model/pending? party-entity)
                                "border-ui-red-100" (party-model/declined? party-entity)
                                "border-ui-dark-gray" (party-model/removed? party-entity))}
       ;; LEFT SIDE COLUMN FOR PARTY DATA
       [:div.flex.flex-1.flex-col.justify-between
        [:div
         (when (and @*integration-contact-modal-open? can-integrate?)
           [search/search-integration-users-modal ctx party-entity target *integration-contact-modal-open?])
         [:div
          [:h3.font-bold.text-sm.m-0 (when no-name? {:class "break-all"})
           (party-model/corporation-and-full-name-or-full-name party-entity lang)]]

         [party-status tr party-entity invite]

         (if (<sub [::ws-subs/loading-config?])
           [:div.mt-2.mr-2 (cc/skeleton-text)]
           [:div.mt-3 {:key (str party-id "-roles")}
            (for-frag [{role-id :db/id :as role} (:party/party-roles party-entity)]
              (let [missing-trade? (contains? (set missing-role-ids) role-id)]
                [:div {:key   (str party-id "-" role-id)
                       :class (when missing-trade? "text-red-500")}
                 (str (party-common/party-role-label role)
                      (when missing-trade? "*"))
                 [:br]]))])]

        [:div.flex.justify-between.items-end.pr-1
         [:div.flex.flex-col.justify-end {:key (str party-id "-contact-info")}
          (when-let [business (:party/business party-entity)]
            (cc/link
              {:href (href/href :view/business {:business-id (:db/id business)})}
              (tr [:party/view-business])))
          [party-email ctx party-entity invite]
          (when active?
            (when-let [phone (party-model/get-user-key ctx party-entity :user/mobile-phone)]
              [:div.w-full.break-words
               [:a {:href (str "tel:" phone)} phone]]))
          (when (and can-integrate? current-int-id)
            [:div.party-integration-pill.party-elem__integration-id
             (str (<sub [::ws-subs/backend-system-name]) " " current-int-id)])]]]

       ;; RIGHT SIDE COLUMN FOR ACTION ICONS
       [:div.flex.flex-0.flex-col.items-center.justify-between
        [:div.flex.flex-0.flex-col.justify-start {:style {:height "25%"}}
         (cond
           (and (not active?)
                (<sub [::process/signal-permitted?
                       target-id
                       [:process.signal/remove-party (util/mmap party-id)]]))
           [:<>
            [edit/remove-party-modal tr party-id target-id false nil]
            [:span
             (cc/icon {:icon        i/trash
                       :description (tr [:party/remove-party])
                       :fill        "var(--ui-blue)"
                       :class-name  "cursor-pointer"
                       :on-click    #(>evt [::edit/toggle-delete-confirm-modal party-id])})]]

           active?
           [:<>
            (when (<sub [::edit/edit-party-open? party-id])
              [edit/edit-party-modal tr party-entity target])
            [:span
             (cc/icon {:icon        i/pen
                       :description (tr [:edit])
                       :fill        "var(--ui-blue)"
                       :class-name  "cursor-pointer"
                       :data-cy     "edit-party"
                       :on-click    (fn [_]
                                      (>evt [::edit/init party-data])
                                      (>evt [::edit/toggle-edit-party-modal party-id]))})]])]

        [:div.flex.flex-0.flex-col.justify-start {:style {:height "25%"}}
         (when (<sub [::process/signal-permitted?
                      target-id
                      [:process.signal/reinvite-party {:party-id party-id}]])
           [ui/modal-confirmation-button {:trigger-icon-opts {:icon        i/refresh-envelope
                                                              :class-name  "cursor-pointer"
                                                              :description (tr [:party/resend-invite])
                                                              :fill        "var(--ui-blue)"}
                                          :modal-title       (tr [:party/resend-invite])
                                          :tr                tr
                                          :on-yes-click      #(>evt [::process/signal
                                                                     target-id
                                                                     [:process.signal/reinvite-party {:party-id party-id}]])}
            [:div (tr [:party/resend-invite-confirm]
                      [(party-model/get-user-key ctx party-entity :user/email)])]])]

        [:div.flex.flex-0.flex-col.justify-start {:style {:height "25%"}}
         (when-let [form (-> party-entity :form/_party first)]
           (cc/icon {:icon        i/document
                     :description (tr [:party/form])
                     :class-name  "cursor-pointer"
                     :fill        "var(--ui-blue)"
                     :on-click    (fn [_]
                                    (>evt [::go-to-party-form
                                           (-> target :entity/parent :workspace/id)
                                           (:db/id form)]))}))]

        [:div.flex.flex-1.flex-col.justify-end {:style {:height "25%"}}
         (when can-integrate?
           (cc/icon {:icon        i/search
                     :description (tr [:party/details])
                     :class-name  "cursor-pointer"
                     :fill        "var(--ui-blue)"
                     :on-click    (fn [_]
                                    (reset! *integration-contact-modal-open? true)
                                    (>evt [::search/init party-id]))}))]]])))
